import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface InvestmentType {
  name: string;
  risk: string;
  returnRate: string;
  icon: string;
  color: string;
}

interface FinancialConcept {
  name: string;
  description: string;
  icon: string;
  color: string;
}

interface SavingRule {
  label: string;
  percent: number;
  color: string;
  description: string;
}

interface FinanceFact {
  label: string;
  icon: string;
}

interface PersonalFinanceTemplateProps {
  topic: string;
  subtitle: string;
  investmentTypes: InvestmentType[];
  concepts: FinancialConcept[];
  savingRules: SavingRule[];
  financeFacts: FinanceFact[];
  compoundData: { year: number; amount: number }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: PersonalFinanceTemplateProps = {
  topic: '个人理财与投资基础',
  subtitle: 'Personal Finance & Investment Basics',
  investmentTypes: [
    {
      name: '股票',
      risk: '高风险',
      returnRate: '8-12%',
      icon: '📈',
      color: '#eab308',
    },
    {
      name: '基金',
      risk: '中风险',
      returnRate: '5-8%',
      icon: '📊',
      color: '#f59e0b',
    },
    {
      name: '债券',
      risk: '低风险',
      returnRate: '3-5%',
      icon: '📜',
      color: '#d97706',
    },
    {
      name: '定期存款',
      risk: '极低风险',
      returnRate: '1.5-2.5%',
      icon: '🏦',
      color: '#b45309',
    },
  ],
  concepts: [
    {
      name: '复利效应',
      description: '利息产生利息，财富指数增长',
      icon: '🔄',
      color: '#fbbf24',
    },
    {
      name: '资产配置',
      description: '分散投资于不同资产类别',
      icon: '🎯',
      color: '#f59e0b',
    },
    {
      name: '风险分散',
      description: '不把所有鸡蛋放在一个篮子里',
      icon: '🛡️',
      color: '#d97706',
    },
    {
      name: '通货膨胀',
      description: '货币购买力随时间下降',
      icon: '💸',
      color: '#b45309',
    },
  ],
  savingRules: [
    { label: '必需开支', percent: 50, color: '#eab308', description: '房租/食物/交通/保险' },
    { label: '个人想要', percent: 30, color: '#f59e0b', description: '娱乐/外出/购物/爱好' },
    { label: '储蓄投资', percent: 20, color: '#fbbf24', description: '应急基金/退休金/投资' },
  ],
  financeFacts: [
    { label: '紧急备用金', icon: '🆘' },
    { label: '定投策略', icon: '📅' },
    { label: '税务规划', icon: '🧾' },
    { label: '保险保障', icon: '☂️' },
  ],
  compoundData: [
    { year: 0, amount: 10000 },
    { year: 5, amount: 16105 },
    { year: 10, amount: 25937 },
    { year: 20, amount: 67275 },
    { year: 30, amount: 174494 },
  ],
  stats: [
    { value: '72法则', label: '年化收益除72≈翻倍年数' },
    { value: '10%', label: '巴菲特推荐指数基金年化' },
    { value: '第8大奇迹', label: '爱因斯坦称复利为' },
  ],
  bottomQuote:
    '理财的核心不在于赚多少，而在于理解金钱的时间价值——让每一分钱都成为你沉睡中的员工',
};

export const PersonalFinanceTemplate: React.FC<
  Partial<PersonalFinanceTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const investStart = 30;
  const conceptStart = 160;
  const curveStart = 200;
  const ruleStart = 280;
  const factStart = 370;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const curveDraw = interpolate(
    frame,
    [curveStart, curveStart + 100],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const compoundCurvePath =
    'M60,300 C160,296 260,284 380,230 S560,90 700,30';

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #1a1505 0%, #0f0a02 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating coin particles */}
      {Array.from({ length: 55 }).map((_, i) => {
        const x = (i * 307) % 1920;
        const y = (i * 173) % 1080;
        const size = 4 + (i % 6);
        const drift = Math.sin(frame * 0.025 + i) * 10;
        const pulse = 0.06 + 0.05 * Math.sin(frame * 0.04 + i * 0.8);
        const colors = ['#eab308', '#fbbf24', '#f59e0b', '#d97706'];
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y + drift,
              width: size,
              height: size,
              borderRadius: '50%',
              background: colors[i % colors.length],
              opacity: pulse,
              boxShadow: `0 0 ${size * 3}px ${colors[i % colors.length]}40`,
              border: `1px solid ${colors[i % colors.length]}50`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#a16207',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#fef9c3',
            flexShrink: 0,
          }}
        >
          💰 理财百科
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#fef9c3',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#a3916b',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Investment Types ===== */}
      <div style={{ position: 'absolute', top: 140, left: 36, width: 500 }}>
        <div
          style={{
            fontSize: 26,
            color: '#a3916b',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          投资类型风险矩阵
        </div>
        <svg width="500" height="650" viewBox="0 0 500 650">
          <defs>
            <linearGradient id="finRiskGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#eab308" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#a16207" stopOpacity="0.05" />
            </linearGradient>
          </defs>

          <rect
            x="20"
            y="10"
            width="460"
            height="630"
            rx="12"
            fill="url(#finRiskGrad)"
            strokeWidth="2"
            stroke="#a1620740"
            opacity={interpolate(frame, [investStart, investStart + 40], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            })}
          />

          {/* Risk axis labels */}
          <text x="30" y="40" fill="#a3916b" fontSize="22" fontWeight="600">
            高风险 / 高回报 ↑
          </text>
          <text x="30" y="620" fill="#a3916b" fontSize="22" fontWeight="600">
            低风险 / 低回报 ↓
          </text>

          {p.investmentTypes.map((inv, i) => {
            const yPos = 90 + i * 135;
            const s = spring({
              frame: frame - (investStart + 20 + i * 14),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            const barWidth = interpolate(
              s,
              [0, 1],
              [0, 60 + (4 - i) * 50],
            );
            return (
              <g key={i} opacity={s}>
                <rect
                  x="50"
                  y={yPos + 10}
                  width={barWidth}
                  height="20"
                  rx="4"
                  fill={inv.color}
                  opacity="0.35"
                />
                <text
                  x="55"
                  y={yPos}
                  fill="#fef9c3"
                  fontSize="22"
                  fontWeight="800"
                >
                  {inv.icon} {inv.name}
                </text>
                <text x="55" y={yPos + 56} fill="#a3916b" fontSize="22">
                  风险: {inv.risk}
                </text>
                <text x="55" y={yPos + 86} fill={inv.color} fontSize="22" fontWeight="700">
                  年化收益: {inv.returnRate}
                </text>

                <line
                  x1="400"
                  y1={yPos - 5}
                  x2="400"
                  y2={yPos + 80}
                  stroke={inv.color}
                  strokeWidth="2"
                  opacity="0.3"
                  strokeDasharray="3 3"
                />
              </g>
            );
          })}
        </svg>
      </div>

      {/* ===== CENTER-LEFT: Core Concepts ===== */}
      <div style={{ position: 'absolute', top: 140, left: 650, width: 500 }}>
        <div
          style={{
            fontSize: 26,
            color: '#a3916b',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          核心理财概念
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.concepts.map((c, i) => {
            const s = spring({
              frame: frame - (conceptStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: '#1a150890',
                  borderRadius: 14,
                  padding: '12px 16px',
                  border: `1.5px solid ${c.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: `${c.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 32,
                    flexShrink: 0,
                    boxShadow: `0 0 18px ${c.color}30`,
                  }}
                >
                  {c.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <span
                      style={{ fontSize: 24, fontWeight: 800, color: c.color }}
                    >
                      {c.name}
                    </span>
                  </div>
                  <div
                    style={{ fontSize: 20, color: '#a3916b', marginTop: 2 }}
                  >
                    {c.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Finance Facts */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 24,
              color: '#a3916b',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            理财要素
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.financeFacts.map((fact, i) => {
              const s = spring({
                frame: frame - (factStart + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: '#1a150890',
                    borderRadius: 12,
                    padding: '10px 14px',
                    border: '1px solid #a1620740',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <span style={{ fontSize: 26, flexShrink: 0 }}>{fact.icon}</span>
                  <span
                    style={{ fontSize: 22, fontWeight: 700, color: '#fbbf24' }}
                  >
                    {fact.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== CENTER-RIGHT: Compound Interest Curve ===== */}
      <div style={{ position: 'absolute', top: 140, left: 1280, width: 590 }}>
        <div
          style={{
            fontSize: 26,
            color: '#a3916b',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          复利增长曲线 · 年化 10%
        </div>
        <svg width="580" height="340" viewBox="0 0 800 340">
          {[60, 120, 180, 240].map((y) => (
            <line
              key={y}
              x1="60"
              y1={y}
              x2="780"
              y2={y}
              stroke="#a16207"
              strokeWidth="1"
              opacity="0.15"
            />
          ))}
          <text x="4" y="310" fill="#a3916b" fontSize="26">
            ¥1万
          </text>
          <text x="4" y="245" fill="#a3916b" fontSize="26">
            ¥5万
          </text>
          <text x="4" y="180" fill="#a3916b" fontSize="26">
            ¥10万
          </text>
          <text x="4" y="110" fill="#a3916b" fontSize="26">
            ¥15万
          </text>
          <text x="4" y="45" fill="#a3916b" fontSize="26">
            ¥17万+
          </text>

          <text x="780" y="335" fill="#a3916b" fontSize="26" textAnchor="end">
            30年 →
          </text>

          <path
            d={compoundCurvePath}
            fill="none"
            stroke="#eab308"
            strokeWidth="4"
            strokeDasharray="1400"
            strokeDashoffset={1400 * (1 - curveDraw)}
            strokeLinecap="round"
          />

          {p.compoundData.map((pt, i) => {
            const cx = 60 + (pt.year / 30) * 700;
            const cy = 300 - (pt.amount / 180000) * 270;
            return (
              <g
                key={i}
                opacity={interpolate(
                  frame,
                  [curveStart + 20 + i * 15, curveStart + 35 + i * 15],
                  [0, 1],
                  { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
                )}
              >
                <circle cx={cx} cy={cy} r="6" fill="#fbbf24" />
                <text
                  x={cx}
                  y={cy - 14}
                  fill="#fef9c3"
                  fontSize="26"
                  fontWeight="700"
                  textAnchor="middle"
                >
                  {pt.year}年
                </text>
              </g>
            );
          })}

          <g
            opacity={interpolate(
              frame,
              [curveStart + 70, curveStart + 90],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            )}
          >
            <text x="560" y="55" fill="#eab308" fontSize="28" fontWeight="700">
              复利曲线加速增长
            </text>
          </g>
        </svg>
      </div>

      {/* ===== 50/30/20 Rule (below Compound Curve) ===== */}
      <div style={{ position: 'absolute', top: 500, left: 1280, width: 590 }}>
        <div
          style={{
            fontSize: 26,
            color: '#a3916b',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          50/30/20 预算法则
        </div>

        {/* Pie-chart-like visual */}
        <svg width="440" height="220" viewBox="0 0 440 220">
          {p.savingRules.map((rule, i) => {
            const s = spring({
              frame: frame - (ruleStart + i * 16),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            const startAngle = i === 0 ? 0 : i === 1 ? 180 : 288;
            const sweep = (rule.percent / 100) * 360;
            const rad1 = (startAngle * Math.PI) / 180;
            const rad2 = ((startAngle + sweep) * Math.PI) / 180;
            const cx = 100;
            const cy = 110;
            const r = 80;
            const x1 = cx + r * Math.cos(rad1);
            const y1 = cy + r * Math.sin(rad1);
            const x2 = cx + r * Math.cos(rad2);
            const y2 = cy + r * Math.sin(rad2);
            const largeArc = sweep > 180 ? 1 : 0;
            const pathD = `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`;
            return (
              <g key={i} opacity={s}>
                <path d={pathD} fill={rule.color} opacity="0.6" />
                <text
                  x="210"
                  y={50 + i * 65}
                  fill={rule.color}
                  fontSize="22"
                  fontWeight="800"
                >
                  {rule.percent}% {rule.label}
                </text>
                <text x="210" y={80 + i * 65} fill="#a3916b" fontSize="22">
                  {rule.description}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#1a150890',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #a1620740',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{ fontSize: 28, fontWeight: 900, color: '#eab308' }}
              >
                {st.value}
              </div>
              <div style={{ fontSize: 22, color: '#a3916b', marginTop: 4 }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#1a150890',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #a16207',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>💡</span>
          <span style={{ fontSize: 20, color: '#fef9c3', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #a16207, #eab308, #fbbf24)',
        }}
      />
    </AbsoluteFill>
  );
};
